import { MFA_TRUSTED_TOKEN_KEY } from "./constants";
import { getSupabaseClient } from "../providers/supabase/supabase";

type TrustedDeviceResponse = {
  trusted: boolean;
  factor_id?: string;
  token?: string;
  expires_at?: string;
};

async function invokeTrustedDevice(
  body: Record<string, unknown>,
): Promise<TrustedDeviceResponse> {
  const { data, error } = await getSupabaseClient().functions.invoke(
    "mfa-trusted-device",
    { body },
  );

  if (error) {
    throw error;
  }

  return data as TrustedDeviceResponse;
}

export function getStoredTrustedToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(MFA_TRUSTED_TOKEN_KEY);
}

export function storeTrustedToken(token: string) {
  window.localStorage.setItem(MFA_TRUSTED_TOKEN_KEY, token);
}

export function clearStoredTrustedToken() {
  window.localStorage.removeItem(MFA_TRUSTED_TOKEN_KEY);
}

export async function markMfaEnrolled(): Promise<void> {
  await invokeTrustedDevice({ action: "mark_enrolled" });
}

export async function registerTrustedDevice(params: {
  factorId: string;
  deviceLabel?: string;
}): Promise<void> {
  const result = await invokeTrustedDevice({
    action: "register",
    factor_id: params.factorId,
    device_label: params.deviceLabel,
  });

  if (!result.token) {
    throw new Error("Trusted device token was not returned");
  }

  storeTrustedToken(result.token);
}

export async function validateTrustedDevice(): Promise<boolean> {
  const token = getStoredTrustedToken();
  if (!token) {
    return false;
  }

  const { data: sessionData } = await getSupabaseClient().auth.getSession();
  if (!sessionData.session) {
    return false;
  }

  try {
    const result = await invokeTrustedDevice({
      action: "validate",
      token,
    });
    return result.trusted === true;
  } catch {
    clearStoredTrustedToken();
    return false;
  }
}

export async function revokeTrustedDevicesForFactor(
  factorId: string,
): Promise<void> {
  try {
    await invokeTrustedDevice({
      action: "revoke",
      factor_id: factorId,
    });
  } catch {
    // Best-effort when unenrolling a factor
  }
}

export async function revokeTrustedDevices(): Promise<void> {
  const token = getStoredTrustedToken();
  clearStoredTrustedToken();

  try {
    await invokeTrustedDevice({
      action: "revoke",
      ...(token ? { token } : {}),
    });
  } catch {
    // Best-effort on logout
  }
}
